/**
 * Dummy data for the design demos.
 *
 * Shapes and vocabulary are taken from the real Warranty domain
 * (`src/types/warranty.d.ts`, `src/Pages/Warranties/Overview.tsx`,
 * `src/properties/warrantiesProperties.ts`) so the demo screens stress the
 * same things production does: long free-text comments, 5-digit CAGE codes,
 * USD costs that need tabular alignment, and status/result pairs.
 *
 * Nothing here talks to an API. It is all hardcoded.
 */

export type WarrantyStatus =
  | 'In process'
  | 'Accepted'
  | 'Rejected'
  | 'Not Claimed';

export type WarrantyResult =
  | 'FOC spare'
  | 'FOC Repair'
  | 'Credit'
  | 'Partial Claim'
  | 'Discount'
  | '';

export type WarrantyClaim = {
  warranty_id: number;
  created_date_text: string;
  fault_barcode: string;
  tail_number: string;
  cage_code_text: string;
  claim_classification_type: string;
  last_comment: string;
  po_number: string;
  part_count: number;
  part_claimed_cost: number;
  manhours_claimed_cost: number;
  shipping_claimed_cost: number;
  total_claimed_cost: number;
  warranty_status: WarrantyStatus;
  claim_request_type: WarrantyResult;
  has_attachment: boolean;
  owner: string;
};

const claim = (
  warranty_id: number,
  created_date_text: string,
  fault_barcode: string,
  tail_number: string,
  cage_code_text: string,
  claim_classification_type: string,
  last_comment: string,
  po_number: string,
  part_count: number,
  part_claimed_cost: number,
  manhours_claimed_cost: number,
  shipping_claimed_cost: number,
  warranty_status: WarrantyStatus,
  claim_request_type: WarrantyResult,
  has_attachment: boolean,
  owner: string,
): WarrantyClaim => ({
  warranty_id,
  created_date_text,
  fault_barcode,
  tail_number,
  cage_code_text,
  claim_classification_type,
  last_comment,
  po_number,
  part_count,
  part_claimed_cost,
  manhours_claimed_cost,
  shipping_claimed_cost,
  total_claimed_cost:
    part_claimed_cost + manhours_claimed_cost + shipping_claimed_cost,
  warranty_status,
  claim_request_type,
  has_attachment,
  owner,
});

export const WARRANTY_CLAIMS: WarrantyClaim[] = [
  claim(4821, '02.09.2026 08:14', 'WO-884213', 'TF-FIA', 'K0176 Lufthansa Technik', 'LHT Contract', 'Vendor acknowledged receipt, awaiting teardown report.', 'PO-551204', 3, 18420, 4260, 780, 'In process', '', true, 'Hafdís B.'),
  claim(4820, '01.09.2026 16:42', 'WO-884198', 'TF-ISR', 'S4821 Collins Aerospace', 'OEM Direct', 'Credit note issued against PO, closing out.', 'PO-551188', 1, 9650, 1820, 410, 'Accepted', 'Credit', true, 'Jón Arnar'),
  claim(4819, '01.09.2026 11:05', 'WO-884170', 'TF-ICE', 'A1093 Safran Landing', 'Repair/Mod', 'Brake assembly outside warranty window by 11 days.', 'PO-551151', 2, 22300, 6140, 1250, 'Rejected', '', false, 'Sigrún H.'),
  claim(4818, '31.08.2026 09:30', 'WO-884102', 'TF-ISW', 'K0176 Lufthansa Technik', 'Service Bulletin', 'SB compliance confirmed, no-charge replacement shipped.', 'PO-551140', 4, 31200, 8900, 2100, 'Accepted', 'FOC spare', true, 'Hafdís B.'),
  claim(4817, '30.08.2026 14:18', 'WO-884067', 'TF-FIU', 'M2204 Honeywell', 'OEM Direct', 'Awaiting RMA number from vendor portal.', 'PO-551122', 1, 7430, 980, 320, 'In process', '', false, 'Ólafur Þ.'),
  claim(4816, '29.08.2026 07:52', 'WO-883991', 'TF-ISO', 'P8812 Parker Hannifin', 'Vendor', 'Partial settlement agreed at 60% of parts value.', 'PO-551098', 2, 14800, 3200, 640, 'Accepted', 'Partial Claim', true, 'Jón Arnar'),
  claim(4815, '28.08.2026 13:27', 'WO-883940', 'TF-ICY', 'R4417 Rolls-Royce', 'Powerplant', 'Borescope images uploaded, engineering review pending.', 'PO-551071', 6, 84600, 21400, 4800, 'In process', '', true, 'Sigrún H.'),
  claim(4814, '27.08.2026 10:11', 'WO-883884', 'TF-ISX', 'S4821 Collins Aerospace', 'Service Letter', 'Unit returned serviceable, no fault found.', 'PO-551044', 1, 5200, 1450, 290, 'Rejected', '', false, 'Ólafur Þ.'),
  claim(4813, '26.08.2026 15:48', 'WO-883812', 'TF-FIR', 'B3320 Boeing', 'MRO', 'Repair quote exceeds threshold, escalated to supervisor.', 'PO-551020', 3, 41750, 12600, 3100, 'In process', '', true, 'Hafdís B.'),
  claim(4812, '26.08.2026 08:03', 'WO-883790', 'TF-ISL', 'A1093 Safran Landing', 'Repair/Mod', 'Wheel hub cracked on delivery — carrier claim filed.', 'PO-551003', 2, 16900, 2400, 1850, 'In process', '', true, 'Jón Arnar'),
  claim(4811, '25.08.2026 12:35', 'WO-883741', 'TF-ICU', 'M2204 Honeywell', 'OEM Direct', 'FOC repair completed and unit reinstalled.', 'PO-550981', 1, 11250, 3840, 520, 'Accepted', 'FOC Repair', false, 'Sigrún H.'),
  claim(4810, '24.08.2026 17:20', 'WO-883688', 'TF-ISP', 'K0176 Lufthansa Technik', 'LHT Contract', 'Covered under pool agreement, no claim raised.', 'PO-550955', 2, 0, 1600, 0, 'Not Claimed', '', false, 'Ólafur Þ.'),
  claim(4809, '23.08.2026 09:44', 'WO-883602', 'TF-ISN', 'T7714 Thales', 'Vendor', 'Vendor disputes installation procedure, evidence requested.', 'PO-550930', 1, 9980, 2760, 430, 'In process', '', true, 'Hafdís B.'),
  claim(4808, '22.08.2026 11:58', 'WO-883554', 'TF-ISD', 'P8812 Parker Hannifin', 'Service Bulletin', 'Discount applied against next order.', 'PO-550911', 3, 19400, 5100, 970, 'Accepted', 'Discount', true, 'Jón Arnar'),
  claim(4807, '21.08.2026 14:06', 'WO-883490', 'TF-FIA', 'S4821 Collins Aerospace', 'OEM Direct', 'Shipped to vendor 19.08, tracking confirmed.', 'PO-550884', 1, 6800, 1200, 380, 'In process', '', false, 'Sigrún H.'),
  claim(4806, '20.08.2026 16:31', 'WO-883425', 'TF-ICE', 'I5509 Icelandair Technical', 'Insurance', 'Ground handling damage, insurer assessing.', 'PO-550860', 5, 52300, 18700, 2400, 'In process', '', true, 'Ólafur Þ.'),
  claim(4805, '19.08.2026 08:22', 'WO-883371', 'TF-ISW', 'R4417 Rolls-Royce', 'Powerplant', 'Normal wear and tear, claim declined by OEM.', 'PO-550838', 2, 28900, 9200, 1700, 'Rejected', '', false, 'Hafdís B.'),
  claim(4804, '18.08.2026 13:49', 'WO-883310', 'TF-ISR', 'B3320 Boeing', 'Service Letter', 'Replacement bracket received, awaiting fitment slot.', 'PO-550812', 1, 3950, 890, 210, 'In process', '', false, 'Jón Arnar'),
  claim(4803, '17.08.2026 10:15', 'WO-883266', 'TF-ISO', 'M2204 Honeywell', 'MRO', 'Full credit approved, invoice reconciled.', 'PO-550790', 2, 13600, 4300, 760, 'Accepted', 'Credit', true, 'Sigrún H.'),
  claim(4802, '16.08.2026 15:02', 'WO-883201', 'TF-FIU', 'T7714 Thales', 'Vendor', 'Unit serial mismatch — paperwork being corrected.', 'PO-550764', 1, 8150, 2100, 390, 'In process', '', true, 'Ólafur Þ.'),
  claim(4801, '15.08.2026 09:38', 'WO-883144', 'TF-ICY', 'A1093 Safran Landing', 'Repair/Mod', 'Outside warranty, cost absorbed by maintenance budget.', 'PO-550741', 3, 24100, 7400, 1180, 'Not Claimed', '', false, 'Hafdís B.'),
  claim(4800, '14.08.2026 12:44', 'WO-883088', 'TF-ISX', 'K0176 Lufthansa Technik', 'LHT Contract', 'Spare issued free of charge under contract terms.', 'PO-550718', 2, 17300, 3600, 820, 'Accepted', 'FOC spare', true, 'Jón Arnar'),
  claim(4799, '13.08.2026 08:57', 'WO-883021', 'TF-ISL', 'P8812 Parker Hannifin', 'OEM Direct', 'Hydraulic actuator leak, teardown scheduled.', 'PO-550694', 1, 10400, 2950, 470, 'In process', '', false, 'Sigrún H.'),
  claim(4798, '12.08.2026 16:12', 'WO-882967', 'TF-FIR', 'I5509 Icelandair Technical', 'Insurance', 'Lightning strike survey complete, claim submitted.', 'PO-550670', 7, 67800, 24300, 3900, 'In process', '', true, 'Ólafur Þ.'),
];

export const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const sumBy = (
  rows: WarrantyClaim[],
  key: keyof WarrantyClaim,
): number => rows.reduce((acc, r) => acc + (r[key] as number), 0);

/**
 * Status → colour.
 *
 * These hues are FIXED by the domain and are reproduced from the project's
 * existing semantic tokens (`src/theme/palette.ts`). They are passed straight
 * through to `StatusChip`, which renders them as a dot rather than a fill, so
 * no theme alters them and no theme has to fight their contrast.
 */
export const STATUS_COLOR: Record<WarrantyStatus, string> = {
  'In process': '#506E9F', // palette.status.inwork
  Accepted: '#2e7d32', // palette.status.complete
  Rejected: '#d32f2f', // palette.status.cancel
  'Not Claimed': '#9D9D9D', // palette.muted
};

export const CLASSIFICATIONS = [
  'LHT Contract',
  'OEM Direct',
  'Service Bulletin',
  'Service Letter',
  'Repair/Mod',
  'MRO',
  'Vendor',
  'Insurance',
  'Powerplant',
];

export const STATUSES: WarrantyStatus[] = [
  'In process',
  'Accepted',
  'Rejected',
  'Not Claimed',
];

export const TAILS = [
  'TF-FIA', 'TF-FIU', 'TF-FIR', 'TF-ICE', 'TF-ICU', 'TF-ICY',
  'TF-ISD', 'TF-ISL', 'TF-ISN', 'TF-ISO', 'TF-ISP', 'TF-ISR',
  'TF-ISW', 'TF-ISX',
];

/** Recent-activity feed for the demo's secondary workflow section. */
export const ACTIVITY = [
  { id: 1, who: 'Jón Arnar', what: 'moved claim #4820 to Accepted', when: '12 min ago', tone: 'ok' as const },
  { id: 2, who: 'Sigrún H.', what: 'uploaded teardown report to #4815', when: '48 min ago', tone: 'info' as const },
  { id: 3, who: 'Hafdís B.', what: 'escalated #4813 above threshold', when: '2 h ago', tone: 'warn' as const },
  { id: 4, who: 'Ólafur Þ.', what: 'closed #4814 as No Fault Found', when: '3 h ago', tone: 'danger' as const },
  { id: 5, who: 'Jón Arnar', what: 'created claim #4812 for TF-ISL', when: '5 h ago', tone: 'info' as const },
];

/** Approval queue for the demo's third workflow section. */
export const APPROVALS = [
  { id: 4813, tail: 'TF-FIR', amount: 57450, age: '3 days', owner: 'Hafdís B.' },
  { id: 4806, tail: 'TF-ICE', amount: 73400, age: '5 days', owner: 'Ólafur Þ.' },
  { id: 4798, tail: 'TF-FIR', amount: 96000, age: '9 days', owner: 'Ólafur Þ.' },
];
